import { Routes, Route, Navigate } from 'react-router-dom';
import { useCity } from './contexts/CityContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { HelmetProvider } from 'react-helmet-async';

import MainLayout from './components/layout/MainLayout';
import HomePage from './pages/HomePage';
import LinesPage from './pages/LinesPage';
import StopsPage from './pages/StopsPage';
import SettingsPage from './pages/SettingsPage';
import DirectionsPage from './pages/DirectionsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import BusLineDetailPage from './pages/BusLineDetailPage';
import MetroLineDetailPage from './pages/MetroLineDetailPage';
import TramLineDetailPage from './pages/TramLineDetailPage';
import TrainLineDetailPage from './pages/TrainLineDetailPage';
import FerryLineDetailPage from './pages/FerryLineDetailPage';
import BusStopDetailPage from './pages/BusStopDetailPage';
import TestPage from './pages/TestPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PremiumPage from './pages/PremiumPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminPanel from './pages/admin/AdminPanel';
import DataMigrationPage from './pages/admin/DataMigrationPage';
import UserProfilePage from './pages/UserProfilePage';
import CommunityPage from './pages/CommunityPage';

// Koruyucu Route bileşeni (özellik aktif değilse ana sayfaya yönlendirir)
const FeatureRoute = ({ 
  featureEnabled, 
  children 
}: { 
  featureEnabled: boolean | undefined, 
  children: React.ReactNode 
}) => {
  if (!featureEnabled) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Uygulama yönlendirme bileşeni
function AppRouter() {
  const { currentCity } = useCity();
  
  return (
    <HelmetProvider>
      <Routes>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePage />} />
          
          {/* Kimlik doğrulama sayfaları */}
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          
          {/* Premium Sayfası */}
          <Route path="premium" element={<PremiumPage />} />
          
          {/* Yol tarifi sayfası - routePlanning özelliği aktifse gösterilir */}
          <Route path="directions" element={
            <FeatureRoute featureEnabled={currentCity?.routePlanning}>
              <DirectionsPage />
            </FeatureRoute>
          } />
          
          {/* Hatlar sayfası - ana sayfa ve ulaşım moduna göre detay sayfaları */}
          <Route path="lines" element={<LinesPage />} />
          
          {/* Yeni ayrılmış ulaşım modu sayfaları */}
          <Route path="lines/bus/:lineId" element={<BusLineDetailPage />} />
          <Route path="lines/metro/:lineId" element={<MetroLineDetailPage />} />
          <Route path="lines/tram/:lineId" element={<TramLineDetailPage />} />
          <Route path="lines/train/:lineId" element={<TrainLineDetailPage />} />
          <Route path="lines/ferry/:lineId" element={<FerryLineDetailPage />} />
          
          {/* Eski URL yapısı için uyumlu yönlendirme */}
          <Route path="lines/:lineId" element={<Navigate to="/lines/bus/:lineId" replace />} />
          
          {/* Duraklar sayfası ve durak detay sayfaları */}
          <Route path="stops" element={<StopsPage />} />
          <Route path="stops/bus/:stopId" element={<BusStopDetailPage />} />
          
          {/* Test sayfası - sadece geliştirme ortamında gösterilir */}
          {import.meta.env.DEV && <Route path="test" element={<TestPage />} />}
          
          {/* Duyurular sayfası - announcements özelliği aktifse gösterilir */}
          <Route path="announcements" element={
            <FeatureRoute featureEnabled={currentCity?.announcements}>
              <AnnouncementsPage />
            </FeatureRoute>
          } />
          
          {/* Yönetici paneli - sadece admin rolündeki kullanıcılar erişebilir */}
          <Route path="admin" element={
            <ProtectedRoute requireAdmin={true}>
              <AdminPanel />
            </ProtectedRoute>
          } />
          <Route path="admin/data-migration" element={<DataMigrationPage />} />
          
          <Route path="settings" element={<SettingsPage />} />
          <Route path="profile/:username" element={<UserProfilePage />} />
          <Route path="community" element={
            <ProtectedRoute>
              <CommunityPage />
            </ProtectedRoute>
          } />
          
          {/* Bulunamayan sayfalar için yönlendirme */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </HelmetProvider>
  );
}

export default AppRouter;